<template>
    <div class="container container--commande">  

        <Loader v-if="!ready"></Loader>

        <article class="commande" v-if="ready">

            <header class="commande__heading">
                <h1 class="woocommerce-order-details__title">Commande {{ commande.numDisplayed }}</h1>
                <div class="commande__heading__date">
                    <p>
                        <strong>Émise le <Date :date="commande.createdAt" /></strong>&nbsp;
                        <CommandeStatus :commande="commande"/>
                    </p>
                </div>
                <div class="facture__heading__actions">
                    <button class="woocommerce-button button alt"
                        type="button"
                        v-if="commande.canBePaidByCarteAchatSB2"
                        @click="pay()">Payer par carte achat</button>
                    <Button class="woocommerce-button button"
                        v-if="commande.isReady"
                        :disabled="commande.isRetrieveLocked"
                        :loading="processingRetrieve"
                        @click.native="retrieve()">         
                        Demander PDF
                        <i class="fa-solid fa-down-to-line"></i>
                    </Button>            

                    <Button class="woocommerce-button button alt"
                        :disabled="downloading || commande.nbDocuments < 1"
                        :loading="!!downloading"
                        v-if="commande.isReady"
                        @click.native="download()">
                        Télécharger
                        <i class="fa-solid fa-down-to-line"></i>
                    </Button>
                </div>
            </header>         
            
            <div class="commande__description">

                <div class="commande__details">

                    <ul class="woocommerce-order-overview woocommerce-thankyou-order-details order_details">
                        <li class="woocommerce-order-overview__order order">Type : <strong>{{ commande.commandeTypeLibelle }}</strong></li>
                        <li class="woocommerce-order-overview__order order">Utilisateur : 
                            <strong v-if="commande.utilisateur">{{ commande.utilisateur.email }}</strong>
                            <strong v-else>—</strong>
                        </li>
                        <li class="woocommerce-order-overview__order order">Référence client : <strong>{{ commande.engagementNum }}</strong></li>
                        <li class="woocommerce-order-overview__order order">Code client : <strong>{{ commande.clientCode }}</strong></li>
                        <li class="woocommerce-order-overview__order order" v-if="commande.budgetCode">Code budget : <strong>{{ commande.budgetCode }}</strong></li>
                        <li class="woocommerce-order-overview__order order" v-if="commande.devis.length">Devis :
                            <strong v-if="commande.devis.length == 1"><router-link :to="{ name: 'devis', params: { id: commande.devis[0].id }}">{{ commande.devis[0].num }}</router-link></strong>
                            <ul v-if="commande.devis.length > 1">
                                <li v-for="devis in commande.devis"
                                    :key="devis.id">
                                    <strong><router-link :to="{ name: 'devis', params: { id: devis.id }}">{{ devis.num }}</router-link></strong>
                                </li>
                            </ul>
                        </li>
                    </ul>

                    <div class="woocommerce-order-details">
                        <div>
                            <Affaire :data="commande.affaire" /> 
                            <strong v-if="commande.vendeur">Suivie par {{ commande.vendeur.name }}<br/></strong>
                        </div>
                        <div v-if="commande.paiement.isCarteAchatSB2">
                            <p v-if="commande.isPaid">
                                <strong>Paiement carte achat</strong><br/>
                                <span>Payé le : <strong v-if="commande.paiement.paidAt"><Date :date="commande.paiement.paidAt" time="true" /></strong><strong v-else>—</strong></span><br/>
                                <span>Montant payé : <strong><Price :amount="commande.paiement.amount " :currency="commande.paiement.currency" /></strong></span><br/>
                                
                                <span v-if="commande.paiement.isCarteAchatSB2">
                                    <span>Référence de la commande (paymentId) : <strong v-if="commande.paiement.transaction">{{ commande.paiement.transaction.num }}</strong><strong v-else>—</strong><br/></span>
                                    <span v-if="commande.paiement.transaction && commande.paiement.transaction.reference">Référence de la transaction (AuthReference) : <strong>{{ commande.paiement.transaction.reference }}</strong><br/></span>
                                    <span v-if="commande.paiement.transaction && commande.paiement.transaction.subject">Identifiant de la transaction (SB2Id) : <strong >{{ commande.paiement.transaction.subject }}</strong><br/></span>                                                                
                                </span>                                
                            </p>
                            <p v-else>
                                <strong>En attente de paiement</strong><br/><br/>
                                <button class="woocommerce-button button alt"
                                    type="button"
                                    v-if="commande.canBePaidByCarteAchatSB2"
                                    @click="pay()">Payer</button>
                            </p>
                        </div>
                        <Livraison 
                            :code="commande.deliveryMode"
                            :dateLibelle="deliveries.length == 1 ? commande.deliveryDateLibelle : ''"
                            :scheduleInfos="commande.deliveryScheduleInfos" 
                            :infos="commande.deliveryInfos" 
                            :indicationsFDV="commande.indicationsFDV"/>
                    </div>

                    <div class="woocommerce-order-details">
                        <h3>Adresse de livraison</h3>
                        <Address :data="commande.deliveryAddress" />
                    </div>

                </div>

            </div>  

            <div class="commande__articles">

                <List class="list--table">

                    <template v-slot:checkboxActions>

                        <button class="woocommerce-button button alt"
                            :disabled="processing || !itemSelected || unpurchasableSelected"
                            v-on:click="buy()">
                            <span class="fa fa-shopping-cart"></span>
                                Ajouter au panier
                        </button>

                        <Button class="alt"
                            :disabled="!itemSelected"
                            :loading="processingDocuments"
                            @click.native="downloadDocs()">         
                            Télécharger les documentations
                            <i class="fa-solid fa-down-to-line"></i>
                        </Button>

                    </template>

                    <template v-slot:heading>                
                        <div class="cell cell--cb">
                            <TogglerAll />
                        </div>
                        <div class="cell cell--ref">Réf.</div>
                        <div class="cell cell--secondary">UV.</div>
                        <div class="cell cell--secondary">Coef. UV</div>
                        <div class="cell cell--main">Libellé</div>
                        <div v-if="currentClient.showDiscount && table.columns.priceRawHTT" class="cell cell--price">Tarif brut</div>
                        <div v-if="currentClient.showDiscount && table.columns.remiseClient" class="cell cell--secondary">% Remise</div>
                        <div class="cell cell--secondary">PU</div>
                        <div class="cell">Qté livrée</div>
                        <div class="cell cell--price">Total HTT</div>
                        <div class="cell cell--secondary">TGAP</div>
                        <div class="cell cell--secondary">TVA</div>       
                    </template>

                    <template v-slot:footer>                
                        <div class="list__footer__row">
                            <div class="cell">Frais de livraison HT</div>
                            <div class="cell"><Price :amount="commande.deliveryCost" :currency="commande.currency" /></div>
                            <div></div>
                        </div>
                        <div v-if="commande.ftc && commande.ftc > 0" class="list__footer__row">
                            <div class="cell">                            
                                {{$setting('FTC_LABEL')}} HT
                            </div>
                            <div class="cell"><Price :amount="commande.ftc" :currency="commande.currency" /></div>
                            <div></div>
                        </div>                        
                        <div class="list__footer__row">
                            <div class="cell">Total HT</div>
                            <div class="cell"><Price :amount="commande.amountHT" :currency="commande.currency" /></div>
                            <div></div>
                        </div>
                        <div class="list__footer__row">
                            <div class="cell">Total TTC</div>
                            <div class="cell"><Price :amount="commande.amountTTC" :currency="commande.currency" /></div>
                        </div>
                    </template>
                    

                    <ul class="list__content" v-bind:class="{ deliveries: deliveries.length > 1  }">

                        <li v-for="delivery in deliveries"
                        :key="delivery.code">
                    
                            <div v-if="deliveries.length > 1" class="list__content__delivery">
                                <i class="fa fa-truck"></i>
                                <strong>{{ delivery.deliveryLibelle }} <Date v-if="delivery.deliveryDate" :date="delivery.deliveryDate" /></strong>
                            </div>                    


                            <ul class="list__content">

                                <li class="list__content__item"
                                    v-for="ligne in delivery.items"
                                    :key="ligne.id">
                    
                                    <ArticleItem
                                        :data="ligne">

                                        <div class="cell cell--cb">
                                            <input type="checkbox"
                                                :disabled="!ligne.articleSlug"
                                                :value="ligne"
                                                v-model="selectedItems"
                                                @click.capture="$event.stopPropagation()" />
                                        </div>
                                        <div class="cell cell--ref">{{ ligne.articleReference }}</div>
                                        <div class="cell cell--secondary">{{ ligne.uvCodeLibelle }}</div>
                                        <div class="cell cell--secondary">x{{ ligne.uvCoef }}</div>
                                        <div class="cell cell--main">{{ ligne.libelle }}</div>
                                        <div v-if="currentClient.showDiscount && table.columns.priceRawHTT" class="cell cell--price"><Price :amount="priceRawHTT(ligne)" :currency="commande.currency" /></div>
                                        <div v-if="currentClient.showDiscount && table.columns.remiseClient" class="cell cell--secondary">{{ remiseClientPct(ligne) }}</div>
                                        <div class="cell cell--secondary"><Price :amount="ligne.priceHTT" :currency="commande.currency" /></div>
                                        <div class="cell cell--qty">{{ ligne.livreeQty }} / {{ ligne.commandeeQty }}</div>
                                        <div class="cell cell--price"><Price :amount="ligne.amountHTT" :currency="commande.currency" /></div>
                                        <div class="cell cell--secondary"><Price :amount="ligne.tgap" :currency="commande.currency" /></div>
                                        <div class="cell cell--secondary"><Tax :rate="ligne.tva" /></div>

                                    </ArticleItem>

                                </li>
                                
                            </ul>

                        </li>
                        
                    </ul>
                </List>

            </div>

        </article>

    </div>
</template>

<script>

import { mapState } from 'vuex'
import api from '@/api/proxyWeb/commandes'
import { goTo } from '@/router'

import cart from '@/containers/cart.vue'
import title from "@/containers/title";

import Address from '@/components/address/Address.vue'
import ArticleItem from '@/components/articleItem/ArticleItem.vue'  
import Button from '@/components/button/Button.vue'
import CommandeStatus from '@/components/commandeStatus/CommandeStatus.vue'
import Date from '@/components/date/Date.vue'
import List, { TogglerAll }  from '@/components/list/List.vue'
import Livraison from '@/components/livraison/Livraison.vue'
import Price from '@/components/price/Price.vue'
import Loader from '@/components/loader/Loader.vue'
import Tax from '@/components/tax/Tax.vue'
import Affaire from '@/components/affaire/Affaire.vue'

import selectAll from '@/containers/selectAll.vue'

export default {

    name: 'Commande',

    data() {
        return {
            downloading: false,
            processingDocuments: false,
            processingRetrieve: false,
            commande: null,
            ready: false              
        }
    },

    computed: {

        ...mapState({
            currentClient: state => state.clients.current
        }),

        deliveries: function() {

            let deliveriesObj = {}

            this.commande.lignes.forEach(item => {

                let delivery
                if ( item.estimatedDeliveryDate) {
                    delivery = {code:item.estimatedDeliveryDateLibelle + item.estimatedDeliveryDate, 
                                deliveryLibelle:item.estimatedDeliveryDateLibelle, 
                                deliveryDate:item.estimatedDeliveryDate}
                }
                else {
                    delivery = {code:'Autre(s) date(s) de livraison(s)', deliveryLibelle: 'Autre(s) date(s) de livraison(s)'}

                }

                if ( deliveriesObj[delivery.code]  === undefined ) {
                    deliveriesObj[delivery.code] = delivery
                    deliveriesObj[delivery.code].items = []
                }
                deliveriesObj[delivery.code].items.push(item)

            });

            let deliveries = Object.values(deliveriesObj) 
        
            return deliveries
        },

        unpurchasableSelected: function() {
            return this.selectedItems.some( commandeLigne => !commandeLigne.isPurchasable )
        }
    },

    methods: {

        buy() {

            let articles = this.selectedItems.map(commandeLigne => {
                    return {
                        'id': commandeLigne.articleId,
                        'quantity': commandeLigne.commandeeQty,
                        'variationId': commandeLigne.articleVariationId
                    }
                })

            this.addToCart(articles);
        },

        cancelAutoUpdate () {
            clearInterval(this.timer);
        },  

        displayPdfRequestInfo () {
            this.$notify.success( 
                'Une version plus récente du fichier PDF sera bientôt mise à votre disposition via le bouton "Télécharger".<br/>Nous vous invitons à revenir dans quelques instants pour télécharger votre fichier.',
                {
                position: "bottom-right",
                duration : 20000,
                type: 'info',
                containerClass: 'toasted-outer',
                className: 'toasted-inner',
                action: {
                    text: 'OK',
                    onClick:(e, toast) => {
                        toast.goAway(0);
                    }
                }  
            })
        },

        download() {

            if ( !! this.downloading || this.commande.nbDocuments < 1 ) return

            this.downloading = true;
            api.getFile(this.commande.id)
                .then(() => this.$notify.success('Téléchargement en cours...') )
                .catch(() => this.$error( 'Impossible de télécharger les documents' ))
                .finally(() => { this.downloading = false } )                
        },

        downloadDocs() {
            this.processingDocuments = true
            api.getDocuments(this.commande.id, this.selectedItems.map( commandeLigne => commandeLigne.num ))
                .then(() => { this.$notify.success('Téléchargement en cours...') })
                .finally(() => { this.processingDocuments = false } )
        },

        pay() {
            goTo( this.$config.SITE_URL + '/wp-content/plugins/myAdelya/tasks/pay/commande/' + this.commande.id )
        },

        priceRawHTT: function(ligne) {
            return ( ligne.priceHTT >= ligne.priceRawHTT ) ? null : ligne.priceRawHTT
        },

        refreshCommande () {
            api.get(this.commande.id).then( (content) => 
            {
                this.commande = content
                if ( !this.commande.isRetrieveLocked ) {
                    this.processingRetrieve = false
                    this.cancelAutoUpdate()
                }
            })
        },

        remiseClientPct: function(ligne) {
            return ( ligne.remiseClient == null || ligne.priceHTT >= ligne.priceRawHTT ) ? null : ligne.remiseClient * 100 + '%'
        },
        
        retrieve() {
            this.processingRetrieve = true
            api.retrieve(this.commande.id)
                .then( content => { 
                    this.commande = content                  
                    this.timer = setInterval(this.refreshCommande, 30000);
                    this.displayPdfRequestInfo()
                })
        }        
    },
    
    created () {

        let promises = [ api.get(this.$route.params.id), api.get(this.$route.params.id + this.$route.meta.tablePath) ]

        Promise
            .all(promises)
            .then( (content) => {
                this.commande = content[0]
                this.table = content[1]
                this.ready = true
                this.setTitle( 'Commande ' + this.commande.numDisplayed ) 
                this.processingRetrieve = this.commande.isRetrieveLocked
                if (this.commande.isRetrieveLocked) {
                    this.timer = setInterval(this.refreshCommande, 30000);
                    this.displayPdfRequestInfo()
                }
            } )
            .catch(error => {
                this.$errorPage(error.reason)
            })

    },

    beforeDestroy () {
      this.cancelAutoUpdate()
    },

    watch:{

        commande: function() {            

            this.selectableItems = []

            if ( !this.commande ) {
                return
            }
            
            this.commande.lignes.forEach(ligne => {
                if ( !ligne.articleSlug ) return
                this.selectableItems.push(ligne)
            })
        }
    },

    components: {
        Address,
        ArticleItem,
        Button,
        CommandeStatus,
        Date,
        Livraison,
        List,
        Loader,
        Price,
        Tax,
        TogglerAll,
        Affaire
    },

    mixins: [ selectAll, title, cart ]
}
</script>

<style scoped>

.deliveries {
    margin-bottom: 1rem;
}

.list__content__delivery {
    line-height: 2.5rem;
    margin-top: 1rem;
    padding: 0.5em 2rem 0;
    background-color: rgb(235, 235, 235);
}


.list__content__delivery i {
    margin-right: 1rem;
    color: gray;
}


</style>

<style>

.commande {
    position: relative;
}

.commande__heading {
    margin-bottom: 2rem;
    padding-top: 4rem; 
}

.commande__heading__actions {
    position: absolute;
    right: 0;
    top: 0;
}

.commande__heading__actions > *:not(:first-child) {
    margin-left: 1rem !important;
}

.commande__footer {
}

.commande__footer__actions {
    text-align: right;
}

.toasted-outer .toasted.toasted-inner {
  font-size: 16px;
  border-radius: 5px;
  font-family: "Open Sans",sans-serif;
}

.toasted .primary .action, .toasted.toasted-primary .action {
    color: #274880 !important;
}

</style>
